import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../lib/api';
import {
  AICourseJob,
  AIGenerateRequest,
  AIGenerateResult,
  AISettings,
  isAITerminal,
} from '../types/aiCourse';

// Backend success envelope: { status: 'success', data, message? }.
interface Envelope<T> {
  status: string;
  data: T;
  message?: string;
}

// ─── Service functions ────────────────────────────────────────────────────

/** Kick off async AI course generation. POST /courses/ai/generate → 202. */
export const generateCourse = async (
  payload: AIGenerateRequest
): Promise<AIGenerateResult> => {
  const res = await api.post<Envelope<AIGenerateResult>>('/courses/ai/generate', payload);
  return res.data.data;
};

/** Poll a generation job's status. GET /courses/ai/jobs/:id */
export const fetchAIJob = async (jobId: string): Promise<AICourseJob> => {
  const res = await api.get<Envelope<AICourseJob>>(`/courses/ai/jobs/${jobId}`);
  return res.data.data;
};

/** Read the tenant's AI opt-in state. GET /settings/ai */
export const fetchAISettings = async (): Promise<AISettings> => {
  const res = await api.get<Envelope<AISettings>>('/settings/ai');
  return res.data.data;
};

/** Enable/disable AI features for the tenant (records consent). PUT /settings/ai */
export const updateAISettings = async (enabled: boolean): Promise<AISettings> => {
  const res = await api.put<Envelope<AISettings>>('/settings/ai', { enabled });
  return res.data.data;
};

// ─── Query keys ──────────────────────────────────────────────────────────

export const aiCourseKeys = {
  all: ['ai-courses'] as const,
  job: (jobId: string) => ['ai-courses', 'job', jobId] as const,
  settings: ['ai-courses', 'settings'] as const,
};

// ─── Hooks ───────────────────────────────────────────────────────────────

export function useAISettings() {
  return useQuery({
    queryKey: aiCourseKeys.settings,
    queryFn: fetchAISettings,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useUpdateAISettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => updateAISettings(enabled),
    onSuccess: (data) => {
      queryClient.setQueryData(aiCourseKeys.settings, data);
      toast.success(data.enabled ? 'AI features enabled' : 'AI features disabled');
    },
    onError: () => {
      toast.error('Could not update AI settings. Please try again.');
    },
  });
}

export function useGenerateCourse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AIGenerateRequest) => generateCourse(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiCourseKeys.all });
    },
    onError: () => {
      toast.error('Could not start AI generation. Please try again.');
    },
  });
}

/**
 * Poll a generation job until it reaches a terminal state. Pass a falsy jobId
 * to disable. Polls every ~3s while queued/running and stops on terminal.
 */
export function useAIJob(jobId: string | null | undefined) {
  return useQuery({
    queryKey: aiCourseKeys.job(jobId ?? ''),
    queryFn: () => fetchAIJob(jobId as string),
    enabled: !!jobId,
    retry: false,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status && isAITerminal(status)) return false;
      return 3000;
    },
  });
}
